import { useState } from 'react';

import { useHumanReadable, useSensitiveData } from 'modules/data';
import { Form } from 'modules/forms';
import { useI18n } from 'modules/i18n';
import { usePathBase } from 'modules/routes';
import { isEmptyHtml, sanitizeHtml } from 'modules/utils';
import PropTypes from 'prop-types';
import { FaPencilAlt } from 'react-icons/fa';

/**
 * Displays record fields and lets users switch to a form to edit, save, or cancel changes.
 * @param {Object} props - Record, field definitions, form defaults, loading state, and save action.
 * @returns {React.ReactElement} Record details or the active edit form.
 */
export default function EditData({
    data,
    formElements,
    sensitiveAttributes = [],
    helpTextCode,
    initialFormValues,
    isLoading = false,
    scope,
    submitData,
}) {
    const [editing, setEditing] = useState(false);
    const { t } = useI18n();
    const { humanReadable } = useHumanReadable();
    const pathBase = usePathBase();

    useSensitiveData(data, sensitiveAttributes);

    function toggleEditing() {
        setEditing((prev) => !prev);
    }

    function handleSave(params) {
        submitData(
            { pathBase },
            params,
            { updateStateBeforeSubmit: true }, // Optimistic UI update
            () => {
                setEditing(false);
            }
        );
    }

    return editing ? (
        <Form
            data={data}
            fetching={isLoading}
            helpTextCode={helpTextCode}
            values={initialFormValues}
            scope={scope}
            onSubmit={(params) => {
                handleSave(params);
            }}
            onCancel={toggleEditing}
            submitText="submit"
            elements={formElements}
        />
    ) : (
        <form className="default">
            <dl className="DescriptionList">
                {formElements.map((element, index) => (
                    <div
                        key={element.key || element.attribute || index}
                        className={`DescriptionList-group ${element.className || ''}`}
                    >
                        <dt className="DescriptionList-term">
                            {t(
                                element.labelKey ||
                                    `activerecord.attributes.${scope}.${element.attribute}`
                            )}
                        </dt>
                        <dd className="DescriptionList-description">
                            {element.elementType === 'richTextEditor'
                                ? (() => {
                                      const content = humanReadable({
                                          obj: data,
                                          attribute: element.attribute,
                                          collapsed: true,
                                      });
                                      const sanitized = sanitizeHtml(
                                          content,
                                          'RICH_TEXT'
                                      );
                                      return isEmptyHtml(sanitized) ? (
                                          '---'
                                      ) : (
                                          <div
                                              dangerouslySetInnerHTML={{
                                                  __html: sanitized,
                                              }}
                                          />
                                      );
                                  })()
                                : humanReadable({
                                      obj: data,
                                      attribute: element.attribute,
                                      collapsed: true,
                                  })}
                        </dd>
                    </div>
                ))}
            </dl>
            <button
                type="button"
                className="Button Button--transparent Button--icon"
                onClick={toggleEditing}
                disabled={isLoading}
            >
                <FaPencilAlt className="Icon Icon--editorial" />{' '}
                {t('edit.default.edit')}
            </button>
        </form>
    );
}

EditData.propTypes = {
    data: PropTypes.object.isRequired,
    formElements: PropTypes.array.isRequired,
    helpTextCode: PropTypes.string,
    initialFormValues: PropTypes.array,
    isLoading: PropTypes.bool,
    scope: PropTypes.string.isRequired,
    sensitiveAttributes: PropTypes.array,
    submitData: PropTypes.func.isRequired,
};
